import React from 'react';

import { AddressManager } from '~src/address/address-manager';
import { ConsolidateCheckbox } from '~src/category/consolidate-checkbox';
import { SessionManager } from '~src/category/session-manager';
import { WidgetLoader } from '~src/category/widget-loader';
import { useInitPostHog } from '~src/commons/services/posthog-service';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { GlobalStateContext, useGlobalState } from '~src/global-state';
import { ReturnAndExchange } from '~src/return-and-exchange/return-and-exchange';
import { RussiaAlert } from '~src/russia-alert';
import { WidgetTheme } from '~src/theme/theme';
import { DebugProxy } from '~src/utils/debug';
import { FirestoreListenerProxy } from '~src/utils/firestore';
import { useIpc } from '~src/utils/ipc-utils';
import { WidgetExpander } from '~src/widget-expander';
import { Footer } from '~src/widget/footer';

import { BillingAddressManagerProxy as BillingAddressManager } from '~address-form/billing-address/billing-address-manager';

const WidgetLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const globalState = useGlobalState();
  // TODO: move the config to global state.
  const session = globalState.sessions[0];

  const { enable_widget_border, enable_transparent_background } = session.flags;

  const layout = (
    <div
      css={theme => ({
        border: enable_widget_border ? `1px solid ${theme.neutral90}` : undefined,
        borderRadius: '6px',
        backgroundColor: enable_transparent_background ? 'transparent' : 'white',
        padding: enable_widget_border ? '24px 16px 24px' : '24px 0 0',
      })}
    >
      {children}
    </div>
  );

  return <WidgetTheme>{layout}</WidgetTheme>;
};

export const Widget: React.FC = () => {
  useInitPostHog();
  const globalState = React.useContext(GlobalStateContext);
  const ipc = useIpc();

  const { locales } = useTranslations();

  const initialized = !!locales && !!globalState;
  React.useEffect(() => {
    if (initialized) {
      const { recipient } = globalState;

      if (!!recipient) {
        ipc.send({ name: 'loaded', data: recipient });
      } else {
        ipc.send({ name: 'loaded' });
      }
    }
  }, [initialized]);

  if (globalState?.purchase_country === 'RU') {
    return <RussiaAlert />;
  }

  if (!!locales && !!globalState) {
    return (
      <WidgetLayout>
        <FirestoreListenerProxy />
        <WidgetLoader />
        <AddressManager />
        <ConsolidateCheckbox />
        <SessionManager />
        <BillingAddressManager />
        <ReturnAndExchange />
        <Footer />
        <WidgetExpander />
        <DebugProxy />
      </WidgetLayout>
    );
  } else {
    // Initially, we have the skeleton loader returned from the HTML snippet
    return null;
  }
};
